import { useEffect, useRef } from 'react';
import { loadPlaces, onPlacesLoaded, placesLib } from '../util/mapsLoader';
import { wirePlacesCombobox } from '../util/placesCombobox';

type PlaceResult = google.maps.places.PlaceResult;

export interface PlacesAutocompleteOptions {
  onPlaceSelected: (place: PlaceResult | undefined) => void;
  /** Maps script language (first load of the page only). */
  language?: string;
  options: google.maps.places.AutocompleteOptions;
  /**
   * What to set the box's `autocomplete` to once Google has set it to "off"
   * (Chrome ignores "off" and lays its own address autofill over Google's
   * list). Same default and same trick as react-google-autocomplete.
   */
  inputAutocompleteValue?: string;
  /**
   * Accessible name of the suggestion list (the box itself is a combobox,
   * see util/placesCombobox).
   */
  listLabel?: string;
}

/** Interactions that mean the visitor is about to use the box. */
const WAKE_EVENTS = ['focus', 'pointerenter', 'touchstart'];

/**
 * Google Places Autocomplete on an input - the part of
 * react-google-autocomplete's `usePlacesWidget` this app used, with one
 * difference: the Maps script is not requested when the box mounts, only
 * when the visitor comes to it (focus, a tap, the pointer over it) or when
 * something else on the page has already loaded it (the hand-off lookup in
 * util/placesLookup, another map). QA 2026-09-25 #14: Maps was ~380 KB of
 * the booking portal's first step, for visitors who often never touch the
 * box because the marketing site handed their address over already.
 *
 * The widget attaches to the same input element either way, so focus and
 * whatever was typed while the script loaded are kept; once attached, a box
 * that already holds text is nudged with an `input` event so the suggestion
 * list opens without another keystroke.
 */
export default function usePlacesAutocomplete<T extends HTMLInputElement>({
  onPlaceSelected,
  language,
  options,
  inputAutocompleteValue = 'new-password',
  listLabel = 'Address suggestions',
}: PlacesAutocompleteOptions) {
  const ref = useRef<T | null>(null);
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  // Latest values, readable from listeners that outlive a render.
  const selectRef = useRef(onPlaceSelected);
  selectRef.current = onPlaceSelected;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const languageRef = useRef(language);
  languageRef.current = language;
  const listLabelRef = useRef(listLabel);
  listLabelRef.current = listLabel;

  useEffect(() => {
    const input = ref.current;
    if (!input) return undefined;
    let alive = true;
    let listener: google.maps.MapsEventListener | null = null;
    let observer: MutationObserver | null = null;
    // A combobox from the first render, before the script is here.
    const combobox = wirePlacesCombobox(input, listLabelRef.current);

    const attach = () => {
      const lib = placesLib();
      if (!alive || autocomplete.current || !lib) return;
      if (window.MutationObserver) {
        observer = new MutationObserver(() => {
          observer?.disconnect();
          input.autocomplete = inputAutocompleteValue;
        });
        observer.observe(input, {
          attributes: true,
          attributeFilter: ['autocomplete'],
        });
      }
      const {
        types, fields, bounds, componentRestrictions, ...rest
      } = optionsRef.current;
      const config: google.maps.places.AutocompleteOptions = {
        ...rest,
        fields,
        types,
        bounds,
      };
      if (componentRestrictions) config.componentRestrictions = componentRestrictions;
      const claimList = combobox.snapshot();
      const ac = new lib.Autocomplete(input, config);
      claimList();
      autocomplete.current = ac;
      listener = ac.addListener('place_changed', () => {
        selectRef.current(ac.getPlace());
      });
      // Typed while the script was on its way: open the list for it.
      if (document.activeElement === input && input.value) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    // Already on the page (or loaded by someone else later): attach now.
    const unsubscribe = onPlacesLoaded(attach);

    const wake = () => {
      WAKE_EVENTS.forEach((e) => input.removeEventListener(e, wake));
      loadPlaces(languageRef.current).then(attach);
    };
    if (!autocomplete.current) {
      WAKE_EVENTS.forEach((e) => input.addEventListener(e, wake, { passive: true }));
      // Focused before this effect ran (autofocus, or a focus() from the page).
      if (document.activeElement === input) wake();
    }

    return () => {
      alive = false;
      unsubscribe();
      WAKE_EVENTS.forEach((e) => input.removeEventListener(e, wake));
      observer?.disconnect();
      combobox.disconnect();
      if (listener) listener.remove();
    };
    // Mount-only, as usePlacesWidget was: the widget belongs to this input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bias moves with the hand-off / the visitor's city.
  const { bounds } = options;
  useEffect(() => {
    if (autocomplete.current && bounds) {
      autocomplete.current.setBounds(bounds);
    }
  }, [bounds]);

  return { ref, autocompleteRef: autocomplete };
}
